



























import { Component, Ref, Vue } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import { MiraInsurerSuggestion } from "@/data/models/MiraInsurerAdministration";
import { dismissInsurerSuggestion } from "@/data/repository/MiraInsurerAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { canDismissWithReason, MAX_REASON_LENGTH } from "@/utils/MiraExceptionAdministration";
import { insurerSuggestionErrorMessage } from "@/utils/MiraInsurerAdministration";

/** What `show()` resolves with: dismissed, refused because the suggestions changed (the caller reloads), or closed. */
export type MiraInsurerSuggestionDismissResult = "DISMISSED" | "RELOAD" | null;

/**
 * VERWERFEN for Mira's suggestion on a review group (#2610125): asks why, then hides the suggestion for
 * the group's spellings. The same proposal never shows again for them; a different one from a later
 * monthly import does. IRES Cloud records who dismissed it, when and why
 * (`dismissInsurerSuggestionForAdministration`).
 */
@Component({ components: { "base-dialog": BaseDialogComponent } })
export default class MiraInsurerSuggestionDismissDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  busy = false;
  reason = "";
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  suggestion: MiraInsurerSuggestion | null = null;
  summary = "";
  expectedImportedAt: number | null = null;

  readonly maxReasonLength = MAX_REASON_LENGTH;

  private resolve: ((result: MiraInsurerSuggestionDismissResult) => void) | null = null;

  get canDismiss(): boolean {
    return canDismissWithReason(this.reason);
  }

  /**
   * @param suggestion - the group's suggestion.
   * @param summary - what it proposes for which name, as the list shows it.
   * @param expectedImportedAt - the import the review was loaded from.
   */
  show(suggestion: MiraInsurerSuggestion, summary: string, expectedImportedAt: number | null): Promise<MiraInsurerSuggestionDismissResult> {
    this.suggestion = suggestion;
    this.summary = summary;
    this.expectedImportedAt = expectedImportedAt;
    this.reason = "";
    this.baseDialog.show({
      title: this.$t("admin.miraInsurerReview.suggestion.dismiss.title").toString(),
      positiveButtonText: this.$t("admin.miraInsurerReview.suggestion.dismiss.action").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 600,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  async onDismiss() {
    if (!this.suggestion || !this.canDismiss) return;
    this.busy = true;
    try {
      await dismissInsurerSuggestion({ texts: this.suggestion.texts, reason: this.reason.trim(), expectedImportedAt: this.expectedImportedAt });
      SnackbarModule.showSuccess({ key: "admin.miraInsurerReview.suggestion.dismissed" });
      this.close("DISMISSED");
    } catch (error) {
      const message = insurerSuggestionErrorMessage(error);
      SnackbarModule.showError(message);
      // Gone, or a monthly import ran since the review loaded: close, so the list shows what is there now.
      if (message.key.endsWith("NO_SUGGESTION") || message.key.endsWith("CHANGED_MEANWHILE")) {
        this.close("RELOAD");
      }
    } finally {
      this.busy = false;
    }
  }

  close(result: MiraInsurerSuggestionDismissResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  finish(result: MiraInsurerSuggestionDismissResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
